import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';

import {
  RELAY_DELIVERY_METHOD,
  relayDeliveryMethodLabel,
  relayStatusLabel,
} from 'common/constants/relayOrders';

import formatDate from '../utils/relayDates';
import Field from './RelayDetailField';
import RelayFaxDocumentDownload from './RelayFaxDocumentDownload';
import RelayShipmentPanel from './RelayShipmentPanel';

/**
 * POM-6099 / POM-6279 P5 — the Summary tab of RelayOrderDetailsDialog, METHOD-AWARE.
 *
 * An API relay (Quello) shows what it always has: the Quello order and script, and Diamond's
 * shipment report. A FAX relay has none of those -- each would be blank forever, and a blank reads
 * as a fault -- so it shows where the documents went and RingCentral's handle for the send
 * instead. Any method this portal has no label for keeps the API layout: the fax fields would be
 * empty for it, which says less than the layout it had before.
 */

const RelayOrderSummary = ({ order, documentCount }) => {
  const isFax = order.deliveryMethod === RELAY_DELIVERY_METHOD.FAX;

  return (
    <Box sx={{ pt: 2 }}>
      <Grid container spacing={2}>
        <Field label='Reference' value={order.externalOrderId} />
        <Field label='Status' value={relayStatusLabel(order.status)} />
        <Field label='Relay Type' value={relayDeliveryMethodLabel(order.deliveryMethod)} />
        {/* Both methods: the detail read selects these for every relay. The pharmacy falls back
            to its id when core has no name for it; the TX reference is null on orders written
            before it existed. */}
        <Field
          label='Fulfilling Pharmacy'
          value={order.fulfillingPharmacyName || order.fulfillingPharmacyId}
        />
        <Field label='TX Reference' value={order.relayRxNumber} />
        {isFax ? (
          <>
            {/* Where a FAX relay went, and the trail of the send. transmissionReference is
                RingCentral's message id -- copyable text, never a link. */}
            <Field label='Destination Fax' value={order.destinationEndpoint} />
            <Field label='Transmission Reference' value={order.transmissionReference} />
            {/* When the one-send CLAIM was taken -- not a confirmed send, hence "Started". Set
                with no transmission reference means the send's outcome is not known; both are
                shown so that reads as it is. */}
            <Field
              label='Fax Send Started'
              value={order.sendClaimedAt && formatDate(order.sendClaimedAt)}
            />
          </>
        ) : (
          <>
            <Field label='Quello Order' value={order.vendorOrderId} />
            <Field label='Quello Script' value={order.vendorScriptNumber} />
          </>
        )}
        <Field label='Transferred' value={formatDate(order.transferredAt)} />
        <Field label='Last Attempt' value={formatDate(order.lastAttemptAt)} />
        {/* PHI, and the one operational PHI field: a stuck order's address answers both a
            state-licensure refusal and an incomplete-address refusal. Shown here, for one order,
            and never as a queue column. */}
        <Field label='Ship To' value={order.shipToAddressText} />
        <Field label='Partner' value={order.partnerId} />
        <Field label='Patient (MPI)' value={order.patientId} />
        {/* Correlation ids, as copyable TEXT and nothing else. No link, no "fetch payload" button,
            not even one wired up later: what they correlate to holds PHI, the engineer's own IAM
            is the access control, and an affordance here is how that control gets routed
            around. */}
        <Field label='Intake Request Id' value={order.intakeRequestId} />
        {/* Both ids, because they answer different questions: intake correlates to the eScript
            that created the order, submit to the attempt that called the vendor -- which is the
            one you need when reconciling against a vendor order. */}
        <Field label='Submit Request Id' value={order.submitRequestId} />
      </Grid>

      {/* Diamond's shipment report. A fax relay never gets one, so it gets no panel at all
          rather than a "not reported yet" notice that would never clear. */}
      {!isFax && <RelayShipmentPanel order={order} />}
      {isFax && (
        <RelayFaxDocumentDownload relayOrderId={order.relayOrderId} documentCount={documentCount} />
      )}
    </Box>
  );
};

const orderShape = PropTypes.shape({
  relayOrderId: PropTypes.string,
  externalOrderId: PropTypes.string,
  status: PropTypes.string,
  deliveryMethod: PropTypes.string,
  vendorOrderId: PropTypes.string,
  vendorScriptNumber: PropTypes.string,
  relayRxNumber: PropTypes.string,
  fulfillingPharmacyId: PropTypes.string,
  fulfillingPharmacyName: PropTypes.string,
  destinationEndpoint: PropTypes.string,
  transmissionReference: PropTypes.string,
  sendClaimedAt: PropTypes.string,
  transferredAt: PropTypes.string,
  lastAttemptAt: PropTypes.string,
  shipToAddressText: PropTypes.string,
  partnerId: PropTypes.string,
  patientId: PropTypes.string,
  intakeRequestId: PropTypes.string,
  submitRequestId: PropTypes.string,
  trackingNumber: PropTypes.string,
  trackingUrl: PropTypes.string,
  carrier: PropTypes.string,
});

// One fax PDF per line item.
RelayOrderSummary.propTypes = { order: orderShape.isRequired, documentCount: PropTypes.number };

RelayOrderSummary.defaultProps = { documentCount: 1 };

export default RelayOrderSummary;
